/**
 * South African mora interest under the Prescribed Rate of Interest Act 55 of 1975.
 * The prescribed rate is the SARB repurchase rate plus 3.5% p.a. (s1(2), as amended
 * by the Judicial Matters Amendment Act 24 of 2015), effective from the first day
 * of the second month after the repo rate changes. Repo moved to 7.00% on
 * 28 May 2026, so the rate is 10.5% p.a. with effect from 1 July 2026.
 * Gazetting lags or is skipped entirely: verify against the latest notice or a
 * rate tracker before relying on it in an outgoing letter, and update this single
 * constant when the repo rate moves. The user's actual rate can be passed in.
 */

export const DEFAULT_PRESCRIBED_RATE = 10.5; // % p.a., effective 1 July 2026
export const PRESCRIBED_RATE_EFFECTIVE = "1 July 2026";

export type MoraCalc = {
  capital: number;
  days: number;
  rate: number;
  interest: number;
  total: number;
};

export const calculateMoraInterest = (
  capital: number,
  dueDate: string | Date,
  rate: number = DEFAULT_PRESCRIBED_RATE,
  asOf: Date = new Date()
): MoraCalc | null => {
  if (!capital || capital <= 0 || !dueDate) return null;
  const due = typeof dueDate === "string" ? new Date(dueDate) : dueDate;
  if (isNaN(due.getTime())) return null;
  const ms = asOf.getTime() - due.getTime();
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  const interest = (capital * (rate / 100) * days) / 365;
  return {
    capital,
    days,
    rate,
    interest,
    total: capital + interest,
  };
};

export const formatZAR = (value: number): string =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
